import { Vec } from "@tldraw/editor";
import {
  loadSrcFromStrokePoints,
  resolveTaper,
  srcCount,
  srcInputX,
  srcInputY,
  srcIsCap,
  srcRadius,
  srcRunningLength,
  srcX,
  srcY,
  srcZ
} from "./core.mjs";
const { PI } = Math;
const FIXED_PI = PI + 1e-4;
const TRACK_TOLERANCE_RATIO = 0.05;
const SIMPLIFY_WINDOW = 8;
const MIN_ROUNDED_CORNER_STEPS = 8;
const MAX_ROUNDED_CORNER_STEPS = 13;
const MIN_CAP_STEPS = 8;
const MAX_CAP_STEPS = 29;
const HARD_CORNER_DPR = -0.62;
let trackCapacity = 1024;
let trackLeftX = new Float64Array(trackCapacity);
let trackLeftY = new Float64Array(trackCapacity);
let trackRightX = new Float64Array(trackCapacity);
let trackRightY = new Float64Array(trackCapacity);
let trackLeftCount = 0;
let trackRightCount = 0;
function growTracks() {
  trackCapacity *= 2;
  const nlx = new Float64Array(trackCapacity);
  nlx.set(trackLeftX);
  trackLeftX = nlx;
  const nly = new Float64Array(trackCapacity);
  nly.set(trackLeftY);
  trackLeftY = nly;
  const nrx = new Float64Array(trackCapacity);
  nrx.set(trackRightX);
  trackRightX = nrx;
  const nry = new Float64Array(trackCapacity);
  nry.set(trackRightY);
  trackRightY = nry;
}
function simplifyTrack(xs, ys, len, tol) {
  if (len <= 2 || tol <= 0) return len;
  const tol2 = tol * tol;
  let out = 1;
  let anchor = 0;
  const lastIdx = len - 1;
  while (anchor < lastIdx) {
    let best = anchor + 1;
    const maxJ = anchor + SIMPLIFY_WINDOW > lastIdx ? lastIdx : anchor + SIMPLIFY_WINDOW;
    const ax = xs[anchor];
    const ay = ys[anchor];
    outer: for (let j = anchor + 2; j <= maxJ; j++) {
      const acx = xs[j] - ax;
      const acy = ys[j] - ay;
      const l2 = acx * acx + acy * acy;
      for (let k = anchor + 1; k < j; k++) {
        let t = l2 === 0 ? 0 : ((xs[k] - ax) * acx + (ys[k] - ay) * acy) / l2;
        t = t < 0 ? 0 : t > 1 ? 1 : t;
        const ex = xs[k] - (ax + acx * t);
        const ey = ys[k] - (ay + acy * t);
        if (ex * ex + ey * ey > tol2) break outer;
      }
      best = j;
    }
    xs[out] = xs[best];
    ys[out] = ys[best];
    out++;
    anchor = best;
  }
  return out;
}
function buildTracks(options, hasAnchor, anchorX, anchorY) {
  const { size = 16, smoothing = 0.5 } = options;
  let lc = 0;
  let rc = 0;
  trackLeftCount = 0;
  trackRightCount = 0;
  const n = srcCount;
  if (n === 0 || size <= 0) return;
  const sx = srcX;
  const sy = srcY;
  const six = srcInputX;
  const siy = srcInputY;
  const sr = srcRadius;
  const srl = srcRunningLength;
  const scap = srcIsCap;
  let lxs = trackLeftX;
  let lys = trackLeftY;
  let rxs = trackRightX;
  let rys = trackRightY;
  const totalLength = srl[n - 1];
  const minDistance = Math.pow(size * smoothing, 2);
  let curVecX = 1;
  let curVecY = 1;
  if (n > 1) {
    const dx = sx[0] - sx[1];
    const dy = sy[0] - sy[1];
    const l = (dx * dx + dy * dy) ** 0.5;
    if (l === 0) {
      curVecX = dx;
      curVecY = dy;
    } else {
      curVecX = dx / l;
      curVecY = dy / l;
    }
  }
  let prevVecX = curVecX;
  let prevVecY = curVecY;
  let plx = sx[0];
  let ply = sy[0];
  let prx = plx;
  let pry = ply;
  let tlx = plx;
  let tly = ply;
  let trx = prx;
  let trY = pry;
  let isPrevPointSharpCorner = false;
  for (let i = 0; i < n; i++) {
    const pointX = sx[i];
    const pointY = sy[i];
    const radius = sr[i];
    const vecX = curVecX;
    const vecY = curVecY;
    let nextVecX = vecX;
    let nextVecY = vecY;
    if (i < n - 1) {
      const fromX = i === 0 && n > 2 && hasAnchor ? anchorX : pointX;
      const fromY = i === 0 && n > 2 && hasAnchor ? anchorY : pointY;
      const dx = fromX - sx[i + 1];
      const dy = fromY - sy[i + 1];
      const l = (dx * dx + dy * dy) ** 0.5;
      if (l === 0) {
        nextVecX = dx;
        nextVecY = dy;
      } else {
        nextVecX = dx / l;
        nextVecY = dy / l;
      }
    }
    curVecX = nextVecX;
    curVecY = nextVecY;
    if (lc + MAX_ROUNDED_CORNER_STEPS + 1 > trackCapacity || rc + MAX_ROUNDED_CORNER_STEPS + 1 > trackCapacity) {
      growTracks();
      lxs = trackLeftX;
      lys = trackLeftY;
      rxs = trackRightX;
      rys = trackRightY;
    }
    const prevDpr = vecX * prevVecX + vecY * prevVecY;
    const nextDpr = i < n - 1 ? nextVecX * vecX + nextVecY * vecY : 1;
    const isPointSharpCorner = prevDpr < 0 && !isPrevPointSharpCorner;
    const isNextPointSharpCorner = nextDpr < 0.2;
    if (isPointSharpCorner || isNextPointSharpCorner) {
      if (nextDpr > HARD_CORNER_DPR && totalLength - srl[i] > radius) {
        const offsetX2 = prevVecX * radius;
        const offsetY2 = prevVecY * radius;
        const cpr = prevVecX * nextVecY - prevVecY * nextVecX;
        if (cpr < 0) {
          tlx = pointX + offsetX2;
          tly = pointY + offsetY2;
          trx = pointX - offsetX2;
          trY = pointY - offsetY2;
        } else {
          tlx = pointX - offsetX2;
          tly = pointY - offsetY2;
          trx = pointX + offsetX2;
          trY = pointY + offsetY2;
        }
        lxs[lc] = tlx;
        lys[lc] = tly;
        lc++;
        rxs[rc] = trx;
        rys[rc] = trY;
        rc++;
      } else {
        const inX = six[i];
        const inY = siy[i];
        const dx = -prevVecY * radius;
        const dy = prevVecX * radius;
        for (let step = 1 / MAX_ROUNDED_CORNER_STEPS, t = 0; t < 1; t += step) {
          let angle = FIXED_PI * t;
          let s = Math.sin(angle);
          let c = Math.cos(angle);
          tlx = inX + (dx * c - dy * s);
          tly = inY + (dx * s + dy * c);
          lxs[lc] = tlx;
          lys[lc] = tly;
          lc++;
          angle = FIXED_PI + FIXED_PI * -t;
          s = Math.sin(angle);
          c = Math.cos(angle);
          trx = inX + (dx * c - dy * s);
          trY = inY + (dx * s + dy * c);
          rxs[rc] = trx;
          rys[rc] = trY;
          rc++;
        }
      }
      plx = tlx;
      ply = tly;
      prx = trx;
      pry = trY;
      if (isNextPointSharpCorner) {
        isPrevPointSharpCorner = true;
      }
      continue;
    }
    isPrevPointSharpCorner = false;
    if (scap[i]) {
      const offsetX2 = vecY * radius;
      const offsetY2 = -vecX * radius;
      lxs[lc] = pointX - offsetX2;
      lys[lc] = pointY - offsetY2;
      lc++;
      rxs[rc] = pointX + offsetX2;
      rys[rc] = pointY + offsetY2;
      rc++;
      continue;
    }
    const lerpedX = nextVecX + (vecX - nextVecX) * nextDpr;
    const lerpedY = nextVecY + (vecY - nextVecY) * nextDpr;
    const offsetX = lerpedY * radius;
    const offsetY = -lerpedX * radius;
    tlx = pointX - offsetX;
    tly = pointY - offsetY;
    if (i <= 1 || (plx - tlx) ** 2 + (ply - tly) ** 2 > minDistance) {
      lxs[lc] = tlx;
      lys[lc] = tly;
      lc++;
      plx = tlx;
      ply = tly;
    }
    trx = pointX + offsetX;
    trY = pointY + offsetY;
    if (i <= 1 || (prx - trx) ** 2 + (pry - trY) ** 2 > minDistance) {
      rxs[rc] = trx;
      rys[rc] = trY;
      rc++;
      prx = trx;
      pry = trY;
    }
    prevVecX = vecX;
    prevVecY = vecY;
  }
  const tolerance = size * TRACK_TOLERANCE_RATIO;
  trackLeftCount = simplifyTrack(trackLeftX, trackLeftY, lc, tolerance);
  trackRightCount = simplifyTrack(trackRightX, trackRightY, rc, tolerance);
}
function getStrokeOutlineTracks(strokePoints, options = {}, vectorAnchor) {
  loadSrcFromStrokePoints(strokePoints);
  buildTracks(
    options,
    !!vectorAnchor,
    vectorAnchor ? vectorAnchor.x : 0,
    vectorAnchor ? vectorAnchor.y : 0
  );
  const lxs = trackLeftX;
  const lys = trackLeftY;
  const rxs = trackRightX;
  const rys = trackRightY;
  const left = new Array(trackLeftCount);
  for (let i = 0; i < trackLeftCount; i++) {
    left[i] = new Vec(lxs[i], lys[i]);
  }
  const right = new Array(trackRightCount);
  for (let i = 0; i < trackRightCount; i++) {
    right[i] = new Vec(rxs[i], rys[i]);
  }
  return { left, right };
}
function arcSteps(radius, sweep, tol, min, max) {
  if (radius <= tol) return min;
  const maxAngle = 2 * Math.acos(1 - tol / radius);
  const steps = Math.ceil(sweep / maxAngle);
  return steps < min ? min : steps > max ? max : steps;
}
function outlineFromSrc(options = {}) {
  const { size = 16, start = {}, end = {}, last: isComplete = false } = options;
  const { cap: capStart = true } = start;
  const { cap: capEnd = true } = end;
  const n = srcCount;
  if (n === 0 || size <= 0) {
    return [];
  }
  const totalLength = srcRunningLength[n - 1];
  const taperStart = resolveTaper(start.taper, size, totalLength);
  const taperEnd = resolveTaper(end.taper, size, totalLength);
  buildTracks(options, false, 0, 0);
  const capTolerance = Math.max(0.05, size * 0.02);
  const firstRadius = srcRadius[0];
  const firstPoint = new Vec(srcX[0], srcY[0], srcZ[0]);
  const lastPoint = n > 1 ? new Vec(srcX[n - 1], srcY[n - 1], srcZ[n - 1]) : Vec.AddXY(firstPoint, 1, 1);
  if (n === 1) {
    if (!(taperStart || taperEnd) || isComplete) {
      const start2 = Vec.Add(
        firstPoint,
        Vec.Sub(firstPoint, lastPoint).uni().per().mul(-firstRadius)
      );
      const dotPts = [];
      const steps = arcSteps(
        firstRadius,
        FIXED_PI * 2,
        capTolerance,
        MIN_ROUNDED_CORNER_STEPS,
        MAX_ROUNDED_CORNER_STEPS
      );
      for (let step = 1 / steps, t = step; t <= 1; t += step) {
        dotPts.push(Vec.RotWith(start2, firstPoint, FIXED_PI * 2 * t));
      }
      return dotPts;
    }
  }
  const startCap = [];
  if (taperStart || taperEnd && n === 1) {
  } else if (capStart) {
    const firstRight = new Vec(trackRightX[0], trackRightY[0]);
    const steps = arcSteps(firstRadius, FIXED_PI, capTolerance, 4, 8);
    for (let step = 1 / steps, t = step; t <= 1; t += step) {
      const pt = Vec.RotWith(firstRight, firstPoint, FIXED_PI * t);
      startCap.push(pt);
    }
  } else {
    const cornersVector = new Vec(trackLeftX[0] - trackRightX[0], trackLeftY[0] - trackRightY[0]);
    const offsetA = Vec.Mul(cornersVector, 0.5);
    const offsetB = Vec.Mul(cornersVector, 0.51);
    startCap.push(
      Vec.Sub(firstPoint, offsetA),
      Vec.Sub(firstPoint, offsetB),
      Vec.Add(firstPoint, offsetB),
      Vec.Add(firstPoint, offsetA)
    );
  }
  const endCap = [];
  const lastRadius = srcRadius[n - 1];
  let lastVecX = 1;
  let lastVecY = 1;
  if (n > 1) {
    const dx = srcX[n - 2] - srcX[n - 1];
    const dy = srcY[n - 2] - srcY[n - 1];
    const l = (dx * dx + dy * dy) ** 0.5;
    if (l === 0) {
      lastVecX = dx;
      lastVecY = dy;
    } else {
      lastVecX = dx / l;
      lastVecY = dy / l;
    }
  }
  const direction = new Vec(-lastVecY, lastVecX);
  if (taperEnd || taperStart && n === 1) {
    endCap.push(lastPoint);
  } else if (capEnd) {
    const start2 = Vec.Add(lastPoint, Vec.Mul(direction, lastRadius));
    const steps = arcSteps(lastRadius, FIXED_PI * 3, capTolerance, MIN_CAP_STEPS, MAX_CAP_STEPS);
    for (let step = 1 / steps, t = step; t < 1; t += step) {
      endCap.push(Vec.RotWith(start2, lastPoint, FIXED_PI * 3 * t));
    }
  } else {
    endCap.push(
      Vec.Add(lastPoint, Vec.Mul(direction, lastRadius)),
      Vec.Add(lastPoint, Vec.Mul(direction, lastRadius * 0.99)),
      Vec.Sub(lastPoint, Vec.Mul(direction, lastRadius * 0.99)),
      Vec.Sub(lastPoint, Vec.Mul(direction, lastRadius))
    );
  }
  const lxs = trackLeftX;
  const lys = trackLeftY;
  const rxs = trackRightX;
  const rys = trackRightY;
  const leftPts = new Array(trackLeftCount);
  for (let i = 0; i < trackLeftCount; i++) {
    leftPts[i] = new Vec(lxs[i], lys[i]);
  }
  const rightPtsReversed = new Array(trackRightCount);
  for (let i = 0; i < trackRightCount; i++) {
    rightPtsReversed[i] = new Vec(rxs[trackRightCount - 1 - i], rys[trackRightCount - 1 - i]);
  }
  return leftPts.concat(endCap, rightPtsReversed, startCap);
}
function getStrokeOutlinePoints(strokePoints, options = {}) {
  loadSrcFromStrokePoints(strokePoints);
  return outlineFromSrc(options);
}
export {
  buildTracks,
  getStrokeOutlinePoints,
  getStrokeOutlineTracks,
  outlineFromSrc,
  trackLeftCount,
  trackLeftX,
  trackLeftY,
  trackRightCount,
  trackRightX,
  trackRightY
};
//# sourceMappingURL=getStrokeOutlinePoints.mjs.map
